import { useQuery } from '@apollo/client';

import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import {
  OWNABLE_PRODUCT_PRODUCT_TYPE_MAP,
  useFindOwnableProductByLegacyProductId,
} from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import GetDegreeById from 'bundles/payments-common/api/GetDegreeById.graphql';
import type {
  GetDegreeByIdQuery as GetDegreeByIdQueryData,
  GetDegreeByIdQueryVariables,
} from 'bundles/payments-common/api/__generated__/GetDegreeById';

export type DegreeInfo = {
  id: string;
  name: string;
  slug: string;
  partnerIds: string[];
};

export const useDegreeProgramInfo = (cart?: CartsV2, skip?: boolean) => {
  const { productItemId, productType } = cart ?? {};

  const ownableProductProductType = productType ? OWNABLE_PRODUCT_PRODUCT_TYPE_MAP[productType] : undefined;
  const { ownableProduct } = useFindOwnableProductByLegacyProductId({
    productType: ownableProductProductType!,
    productItemId: productItemId!,
    skip: skip || !(ownableProductProductType && productItemId),
  });

  const underlyingProductId = ownableProduct.underlyingProductItemId;
  const numberOfCycles = ownableProduct.numberOfInstallments;

  const { data: degreeProgramData } = useQuery<GetDegreeByIdQueryData, GetDegreeByIdQueryVariables>(GetDegreeById, {
    variables: { id: underlyingProductId! },
    skip: skip || !underlyingProductId,
    context: { clientName: 'gatewayGql' },
  });

  const degreeData = degreeProgramData?.Degree?.queryById;
  const degreeProgram = degreeData
    ? {
        id: degreeData.id,
        name: degreeData.name,
        slug: degreeData.slug,
        partnerIds: degreeData.partners.map((partner) => partner.id),
      }
    : undefined;

  return {
    underlyingProductId,
    numberOfCycles,
    degreeProgram,
  };
};
